import React, { useEffect, useReducer, useState } from 'react';
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { FormControl } from '@mui/material';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import CustomTextField from '../../../../../shared/form-controls/custom-text-field/Custom-Text-Field';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { VALID_INTEGER } from '../../../../../utils/formUtils';
import KeyValuePairList from '../../../../../shared/key-value-pair-list/KeyValuePairList';
import { useDispatch } from 'react-redux';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../../utils/loaderSlice';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import { createPreferenceAPI, updatePreferenceAPI } from '../../../../../utils/ServicesAPIUtils';
import { fetchAllAccommodations } from '../../../../property/propertySlice';
import CustomChip from '../../../../../shared/form-controls/custom-chip/Custom-Chip';

const PreferenceCreate = (props) => {
  const { toggleDrawer, obj, hotelId, editing } = props;
  const [tabValue, setTabValue] = useState(0);
  const [itemList, setItemList] = useState([]);
  const [edit, setEdit] = useState(false);
  const [itemIndex, setItemIndex] = useState('');
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [duplicateNameError, setDuplicateNameError] = useState('');
  const [duplicateCodeError, setDuplicateCodeError] = useState('');

  const [formData, setFormData] = useReducer((state, newState) => ({ ...state, ...newState }), {
    name: {
      value: '',
      fieldName: 'Name',
      errors: {},
      validations: ['required'],
      tab: 0,
    },
    code: {
      value: '',
      fieldName: 'Preference Code',
      errors: {},
      validations: ['required', 'validAlphaNumeric'],
      tab: 0,
    },
    description: {
      value: '',
      fieldName: 'Description',
      errors: {},
      validations: [],
      tab: 0,
    },
    isActive: {
      value: false,
      fieldName: '',
      errors: {},
      validations: [],
      tab: 0,
    },
    allowMultipleSelection: {
      value: 'ChooseOne',
      fieldName: 'allowMultipleSelection',
      validations: [],
      tab: 0,
    },
    customAttributes: {
      value: [],
      fieldName: 'Custom Attributes',
      errors: {},
      validations: [],
      tab: 0,
    },
    isItemActive: {
      value: 'Inactive',
      fieldName: 'Item Active Status',
      errors: {},
      validations: [],
      tab: 1,
    },
    itemName: {
      value: '',
      fieldName: 'Item Name',
      errors: {},
      validations: ['required'],
      tab: 1,
    },
    itemId: {
      value: '',
      fieldName: 'Item Code',
      errors: {},
      validations: [],
      tab: 1,
    },
    itemDescription: {
      value: '',
      fieldName: 'Description',
      errors: {},
      validations: [],
      tab: 1,
    },
    itemQuantity: {
      value: 'Inactive',
      fieldName: 'Quantity',
      errors: {},
      validations: [],
      tab: 1,
    },
    itemActive: {
      value: true,
      fieldName: 'Item Active Status',
      errors: {},
      validations: [],
      tab: 1,
    },
  });

  const validateDetailsTab = () => {
    let valid = true;
    const errors = {};
    if (!formData.name.value.trim()) {
      errors.name = { ...formData.name, errors: { required: 'Name is required' } };
      valid = false;
    }
    const codeValue = formData.code.value.trim();
    if (!codeValue) {
      errors.code = { ...formData.code, errors: { required: 'Code is required' } };
      valid = false;
    } else if (!/^[A-Za-z0-9\- ]+$/.test(codeValue)) {
      errors.code = {
        ...formData.code,
        errors: {
          validAlphaNumeric: "Preference Code can not contain special character except '-'",
        },
      };
      valid = false;
    }
    if (!valid) {
      setFormData(errors);
    }
    return valid;
  };

  useEffect(() => {
    if (formData.itemName.value) setDuplicateNameError('');
    if (formData.itemId.value) setDuplicateCodeError('');
  }, [formData.itemName.value, formData.itemId.value]);

  useEffect(() => {
    if (editing && obj) {
      setFormData({
        name: { ...formData.name, value: obj.name },
        code: { ...formData.code, value: obj.code },
        description: { ...formData.description, value: obj.description },
        isActive: { ...formData.isActive, value: obj.isActive },
        allowMultipleSelection: {
          ...formData.allowMultipleSelection,
          value: obj.allowMultipleSelection ? 'chooseAllThatApply' : 'ChooseOne',
        },
        customAttributes: { ...formData.customAttributes, value: obj.customAttributes || [] },
      });
      setItemList(obj.preferenceItems || []);
    }
  }, [editing, obj]);

  const handleTabChange = (event, newValue) => {
    if (tabValue === 0 && newValue === 1) {
      if (!validateDetailsTab()) {
        return;
      }
    }
    setTabValue(newValue);
  };

  const onCancel = () => {
    toggleDrawer('', false);
  };

  const onItemAdd = () => {
    const nameToAdd = formData.itemName.value.trim().toLowerCase();
    const codeToAdd = formData.itemId.value.trim().toLowerCase();

    const nameDup = itemList.some((i) => i.name.trim().toLowerCase() === nameToAdd);
    const codeDup = codeToAdd && itemList.some((i) => i.code.trim().toLowerCase() === codeToAdd);

    if (nameDup || codeDup) {
      if (nameDup) setDuplicateNameError('An item with this name has already been added');
      if (codeDup) setDuplicateCodeError('An item with this code has already been added');
      return;
    }

    setDuplicateNameError('');
    setDuplicateCodeError('');

    const newItem = {
      name: formData.itemName.value,
      code: formData.itemId.value,
      description: formData.itemDescription.value,
      isActive: formData.itemActive.value,
    };
    setItemList([...itemList, newItem]);
    resetItemForm();
  };

  const onItemEdit = (index) => {
    setEdit(true);
    setItemIndex(index);
    const item = itemList[index];
    setFormData({
      itemName: { ...formData.itemName, value: item.name },
      itemId: { ...formData.itemId, value: item.code },
      itemDescription: { ...formData.itemDescription, value: item.description },
      itemActive: { ...formData.itemActive, value: item.isActive },
    });
  };

  const onItemUpdate = () => {
    const nameToUpdate = formData.itemName.value.trim().toLowerCase();
    const codeToUpdate = formData.itemId.value.trim().toLowerCase();

    const nameDup = itemList.some(
      (item, idx) => idx !== itemIndex && item.name.trim().toLowerCase() === nameToUpdate,
    );
    const codeDup =
      codeToUpdate &&
      itemList.some(
        (item, idx) => idx !== itemIndex && item.code.trim().toLowerCase() === codeToUpdate,
      );

    if (nameDup || codeDup) {
      if (nameDup) setDuplicateNameError('An item with this name has already been added');
      if (codeDup) setDuplicateCodeError('An item with this code has already been added');
      return;
    }

    setDuplicateNameError('');
    setDuplicateCodeError('');

    const updatedItems = [...itemList];
    updatedItems[itemIndex] = {
      name: formData.itemName.value,
      code: formData.itemId.value,
      description: formData.itemDescription.value,
      isActive: formData.itemActive.value,
    };
    setItemList(updatedItems);
    setEdit(false);
    resetItemForm();
  };

  const onItemDelete = (index) => {
    const updatedItems = itemList.filter((_, i) => i !== index);
    setItemList(updatedItems);
  };

  const resetItemForm = () => {
    setFormData({
      itemName: { ...formData.itemName, value: '' },
      itemId: { ...formData.itemId, value: '' },
      itemDescription: { ...formData.itemDescription, value: '' },
      itemActive: { ...formData.itemActive, value: true },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const lang = decryptLocalStorage('lang') !== 'en' ? decryptLocalStorage('lang') : '';

    const payload = {
      hotelId,
      name: formData.name.value,
      code: formData.code.value,
      description: formData.description.value || '',
      isActive: formData.isActive.value,
      allowMultipleSelection: formData.allowMultipleSelection.value === 'chooseAllThatApply',
      customAttributes: formData.customAttributes.value,
      preferenceItems: itemList,
      lang,
    };

    try {
      dispatch(stepUpLoaderCount());
      setLoading(true);

      if (editing && obj?.id) {
        await updatePreferenceAPI({ ...payload, id: obj.id });
        notificationManager('success', 'Preference updated successfully');
      } else {
        await createPreferenceAPI(payload);
        notificationManager('success', 'Preference created successfully');
      }
      await dispatch(fetchAllAccommodations(hotelId));
      toggleDrawer('', false);
    } catch (err) {
      const msg =
        err?.graphQLErrors?.[0]?.message || (editing ? 'Update failed' : 'Creation failed');
      notificationManager('danger', msg);
    } finally {
      setLoading(false);
      dispatch(stepDownLoaderCount());
    }
  };

  const onDragEnd = (dragResult) => {
    if (!dragResult.destination) return;

    const reorderedItems = Array.from(itemList);
    const [removed] = reorderedItems.splice(dragResult.source.index, 1);
    reorderedItems.splice(dragResult.destination.index, 0, removed);

    setItemList(reorderedItems);
  };

  const cancelEdit = () => {
    setEdit(false);
    resetItemForm();
  };
  return (
    <form className='category-create'>
      <div className='container d-flex align-items-center flex-column '>
        <div className='row px-3 w-100 mt-4 '>
          <div className='col-md-5'>
            <span className='page-title'>{editing ? 'Edit' : 'Add'} Preference</span>
          </div>
          <div className='col-md-6 '>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant='fullWidth'
              indicatorColor='primary'
              textColor='primary'
              aria-label='roomTypes-add-tabs'
              className='property-tabs'
            >
              <Tab label='Details' />
              <Tab label='Items' />
            </Tabs>
          </div>
          <div className='col-md-1 d-flex justify-content-end'>
            <IconButton onClick={onCancel}>
              <CancelIcon />
            </IconButton>
          </div>
        </div>

        {tabValue === 0 && (
          <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
            <div className='row px-3 w-100 mt-4'>
              <div className='col-md-6'>
                <span className='page-title-secondary'>Details</span>
              </div>
            </div>{' '}
            <div className='row px-3 w-100 d-flex mt-3'>
              <div className='col-md-6 mb-3'>
                <CustomTextField
                  item={formData.name}
                  handleInputChange={(e) => setFormData({ name: e })}
                  maxLength='100'
                  endAdornment={true}
                  characterCount={true}
                  multiline={true}
                />
              </div>
              <div className='col-md-6 mb-3'>
                <CustomTextField
                  item={formData.code}
                  handleInputChange={(e) => {
                    setFormData({ code: e });
                  }}
                  maxLength='30'
                  endAdornment={true}
                  characterCount={true}
                />
              </div>

              <div className='row'>
                <div className='col-md-12 mb-3'>
                  <CustomTextField
                    item={formData.description}
                    handleInputChange={(e) => setFormData({ description: e })}
                    multiline={true}
                  />
                </div>
              </div>
              <div className='col-md-12 mb-3'>
                <div className='d-flex align-items-center mt-1 ' onClick={(e) => e.stopPropagation()}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.isActive.value}
                        onChange={(e) =>
                          setFormData({
                            isActive: { ...formData.isActive, value: e.target.checked },
                          })
                        }
                      />
                    }
                    label='Active'
                  />
                </FormGroup>
                </div>
              </div>
              <div className='col-md-6 mt-4'>
                <div className='align-items-center mb-4'>
                  <h4 className='tab-title'>Preference Type</h4>
                  <div className='d-flex justify-content-between'>
                    <FormControl>
                      <RadioGroup
                        row
                        aria-labelledby='demo-radio-buttons-group-label'
                        defaultValue={formData.allowMultipleSelection.value}
                        value={formData.allowMultipleSelection.value}
                        name='radio-buttons-group'
                        onChange={(e) => {
                          const allowMultipleSelection = {
                            value: e.target.value,
                            fieldName: 'allowMultipleSelection',
                            validations: [],
                            tab: 0,
                          };
                          setFormData({ allowMultipleSelection });
                        }}
                      >
                        <FormControlLabel
                          value='ChooseOne'
                          control={<Radio />}
                          label='Choose one'
                        />
                        <FormControlLabel
                          value='chooseAllThatApply'
                          control={<Radio className='radio' />}
                          label='Choose all that apply'
                        />
                      </RadioGroup>
                    </FormControl>
                  </div>
                </div>
              </div>
              <KeyValuePairList
                item={formData.customAttributes}
                onListChange={(e) => {
                  setFormData({ customAttributes: e });
                }}
                className='mt-4'
              />
            </div>
          </div>
        )}

        {tabValue === 1 && (
          <div className='row px-3 w-100 d-flex mt-4'>
            <div>
              <h4 className='tab-title'>Items</h4>
              <div>
                <div className='row'>
                  <div className='col-md-6 mb-3'>
                    <CustomTextField
                      item={formData.itemName}
                      handleInputChange={(e) => setFormData({ itemName: e })}
                      maxLength='100'
                      multiline={true}
                      endAdornment={true}
                      characterCount={true}
                    />
                  </div>
                  <div className='col-md-6 mb-3'>
                    <CustomTextField
                      item={formData.itemId}
                      handleInputChange={(e) => setFormData({ itemId: e })}
                      maxLength='30'
                      multiline={true}
                      endAdornment={true}
                      characterCount={true}
                    />
                  </div>
                  <div className='col-md-12 mb-3'>
                    <CustomTextField
                      item={formData.itemDescription}
                      handleInputChange={(e) => setFormData({ itemDescription: e })}
                      multiline={true}
                    />
                  </div>
                  <div className='col-md-12 mb-3'>
                    <div
                      className='d-flex align-items-center mt-1 '
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FormGroup>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.itemActive.value}
                              onChange={(e) =>
                                setFormData({
                                  itemActive: { ...formData.itemActive, value: e.target.checked },
                                })
                              }
                            />
                          }
                          label='Active'
                        />
                      </FormGroup>
                    </div>
                  </div>
                  <div className='col-md-12 mb-3'>
                    {edit ? (
                      <>
                        <Button
                          variant='outlined'
                          color='primary'
                          onClick={onItemUpdate}
                          disabled={
                            !formData.itemName.value || !!duplicateNameError || !!duplicateCodeError
                          }
                        >
                          Update
                        </Button>
                        <Button
                          variant='text'
                          color='secondary'
                          onClick={cancelEdit}
                          className='ms-2'
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant='outlined'
                        color='primary'
                        onClick={onItemAdd}
                        disabled={
                          !formData.itemName.value || !!duplicateNameError || !!duplicateCodeError
                        }
                      >
                        Add
                      </Button>
                    )}
                    {duplicateNameError && (
                      <div className='field-error mt-2'>{duplicateNameError}</div>
                    )}
                    {duplicateCodeError && (
                      <div className='field-error mt-2'>{duplicateCodeError}</div>
                    )}
                  </div>
                </div>

                {itemList.length > 0 && (
                  <div className='mt-4'>
                    <div className='col-md-11 mt-3 ps-2 item-list-container'>
                      <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId='droppableItems'>
                          {(provided) => (
                            <div
                              {...provided.droppableProps}
                              className='col-md-11'
                              ref={provided.innerRef}
                            >
                              {itemList.map((item, index) => (
                                <Draggable key={index} draggableId={'dragId' + index} index={index}>
                                  {(provided) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className='col row custom-duration-box'
                                    >
                                      <div className='row mx-2 align-items-center'>
                                        <IconButton
                                          aria-label='drag'
                                          disabled={true}
                                          className='col-1'
                                        >
                                          <DragIndicatorIcon fontSize='large' />
                                        </IconButton>
                                        <div className='col-md-5 text w-75'>
                                          <div className='d-flex align-items-center'>
                                            <div className='col-md-5 text w-75'>
                                              <TextField
                                                label='Item Name'
                                                value={item?.name}
                                                InputProps={{ readOnly: true }}
                                                className='me-2'
                                              />
                                            </div>
                                            <CustomChip
                                              label={item.isActive ? 'Active' : 'Inactive'}
                                              color={item.isActive ? 'primary' : 'default'}
                                            />
                                          </div>
                                        </div>
                                        <div className='col-md-1'>
                                          <IconButton onClick={() => onItemEdit(index)}>
                                            <EditIcon fontSize='small' className='icon' />
                                          </IconButton>
                                        </div>
                                        <div className='col-md-1'>
                                          <IconButton onClick={() => onItemDelete(index)}>
                                            <RemoveCircleIcon fontSize='small' className='icon' />
                                          </IconButton>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
          <div className='col-md-12 d-flex justify-content-start'>
            {tabValue < 1 ? (
              <Button
                variant='contained'
                className='me-3'
                size='large'
                color='primary'
                onClick={() => {
                  if (validateDetailsTab()) setTabValue(1);
                }}
                disabled={
                  !formData?.name?.value?.trim() ||
                  !formData?.code?.value?.trim() ||
                  !!formData?.code?.errors?.validAlphaNumeric
                }
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                variant='contained'
                color='primary'
                size='large'
                className='me-3'
                disabled={
                  loading ||
                  itemList.length === 0 ||
                  edit ||
                  !!duplicateNameError ||
                  !!duplicateCodeError
                }
              >
                {editing ? 'Update' : 'Add'}
              </Button>
            )}
            <Button
              variant='outlined'
              type='button'
              color='secondary'
              size='large'
              onClick={onCancel}
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default PreferenceCreate;
